// Formatted text, as a learner reads it: a lesson's text blocks, a homework
// brief, a practice's instruction.
//
// The HTML was written in the staff panel's editor and is sanitised by the
// server on the way in and on the way out (internal/content). It is cut down
// to the same allow-list here once more before it is put on the page, so a
// copy that slipped past is still harmless. Spoilers — Telegram's hidden text,
// `<span data-spoiler="true">` — stay covered until they are tapped or opened
// from the keyboard; a checklist's boxes are shown but cannot be ticked; links
// open in a new tab.

import { useMemo } from 'react'
import type { KeyboardEvent, MouseEvent } from 'react'

import { useT } from '../i18n'
import richtext from '../i18n/richtext'
import '../styles/richtext.css'

export function RichText({ html, className }: { html: string; className?: string }) {
  const t = useT(richtext)
  const reveal = t('revealSpoiler')
  const clean = useMemo(() => sanitizeRichText(html, reveal), [html, reveal])

  // One listener for every spoiler in the text: the markup is set as HTML, so
  // there is no element of React's to put one on.
  function open(target: EventTarget | null, root: HTMLElement): boolean {
    const spoiler = target instanceof Element ? target.closest('[data-spoiler]') : null
    if (!spoiler || !root.contains(spoiler) || spoiler.hasAttribute('data-revealed')) return false
    spoiler.setAttribute('data-revealed', 'true')
    spoiler.removeAttribute('role')
    spoiler.removeAttribute('aria-label')
    // Still focusable from code, so a keyboard user is not dropped to the top
    // of the page; no longer a stop for Tab.
    spoiler.setAttribute('tabindex', '-1')
    return true
  }

  return (
    <div
      className={className ? `rich ${className}` : 'rich'}
      dangerouslySetInnerHTML={{ __html: clean }}
      onClick={(e: MouseEvent<HTMLDivElement>) => {
        if (open(e.target, e.currentTarget)) e.preventDefault()
      }}
      onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
        if ((e.key === 'Enter' || e.key === ' ') && open(e.target, e.currentTarget)) e.preventDefault()
      }}
    />
  )
}

// ------------------------------------------------------------- allow-list --

/** Elements kept, as internal/content's sanitiser keeps them. */
const KEEP = new Set([
  'p', 'br', 'h2', 'h3', 'h4', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'blockquote', 'code', 'pre', 'ul', 'ol',
  'li', 'div', 'span', 'label', 'a', 'input',
])

/** Elements dropped with everything inside them; any other unknown one is unwrapped. */
const DROP = new Set([
  'script', 'style', 'template', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet', 'noscript', 'noembed',
  'noframes', 'svg', 'math', 'textarea', 'select', 'option', 'button', 'form', 'head', 'title', 'meta', 'link',
  'base', 'img', 'picture', 'video', 'audio', 'source', 'track', 'canvas', 'map', 'area', 'xmp', 'plaintext',
  'portal', 'dialog',
])

const SAFE_HREF = /^(https?:|mailto:|tel:)/i

/**
 * The HTML cut down to the allow-list: every other element unwrapped (or
 * dropped with its content, when its content is code), every attribute
 * outside the list removed, links opening in a new tab, checkboxes read-only,
 * and each spoiler made a button that says what it hides behind `reveal`.
 */
function sanitizeRichText(html: string, reveal: string): string {
  if (html.trim() === '') return ''
  // A document from DOMParser is inert: nothing in it loads or runs.
  const body = new DOMParser().parseFromString(html, 'text/html').body
  const doc = body.ownerDocument
  const out = doc.createElement('div')
  copyChildren(body, out, doc, reveal)
  return out.innerHTML
}

function copyChildren(from: Node, to: Node, doc: Document, reveal: string) {
  for (const child of Array.from(from.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      to.appendChild(doc.createTextNode(child.textContent ?? ''))
      continue
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue
    const el = child as Element
    const tag = el.localName.toLowerCase()
    if (DROP.has(tag) || el.namespaceURI !== 'http://www.w3.org/1999/xhtml') continue
    if (!KEEP.has(tag)) {
      copyChildren(el, to, doc, reveal)
      continue
    }
    const copy = cleanElement(el, tag, doc, reveal)
    if (!copy) continue
    to.appendChild(copy)
    if (tag !== 'input' && tag !== 'br') copyChildren(el, copy, doc, reveal)
  }
}

function cleanElement(el: Element, tag: string, doc: Document, reveal: string): Element | null {
  switch (tag) {
    case 'a': {
      const copy = doc.createElement('a')
      const href = (el.getAttribute('href') ?? '').replace(/[\u0000- \u007f-\u009f]/g, '')
      if (SAFE_HREF.test(href)) {
        copy.setAttribute('href', href)
        copy.setAttribute('target', '_blank')
        copy.setAttribute('rel', 'noopener noreferrer nofollow')
      }
      return copy
    }
    case 'span': {
      const copy = doc.createElement('span')
      if (el.getAttribute('data-spoiler') === 'true') {
        copy.setAttribute('data-spoiler', 'true')
        copy.setAttribute('role', 'button')
        copy.setAttribute('tabindex', '0')
        copy.setAttribute('aria-label', reveal)
      }
      return copy
    }
    case 'ul': {
      const copy = doc.createElement('ul')
      if (el.getAttribute('data-type') === 'taskList') copy.setAttribute('data-type', 'taskList')
      return copy
    }
    case 'li': {
      const copy = doc.createElement('li')
      if (el.getAttribute('data-type') === 'taskItem') {
        copy.setAttribute('data-type', 'taskItem')
        copy.setAttribute('data-checked', el.getAttribute('data-checked') === 'true' ? 'true' : 'false')
      }
      return copy
    }
    case 'input': {
      if ((el.getAttribute('type') ?? '').toLowerCase() !== 'checkbox') return null
      const copy = doc.createElement('input')
      copy.setAttribute('type', 'checkbox')
      if (el.hasAttribute('checked')) copy.setAttribute('checked', '')
      copy.setAttribute('disabled', '')
      return copy
    }
    default:
      return doc.createElement(tag)
  }
}
